/**
 * saveThemeWorkspace — the persistence channel for the staged colors/typography of an UNPUBLISHED theme
 * (feature 0034 Bloc 1).
 *
 * These settings used to live ONLY in the builder's `localStorage`, which cost us two things:
 *  1. The work was lost on another machine, another browser, or a site-data clear — silently.
 *  2. The SERVER had no idea they existed, so a storefront preview of an unpublished theme could only ever
 *     render that theme's layout in the LIVE theme's colors (0034 §3.1) — which is why this block lands
 *     before the preview endpoints.
 *
 * They now persist under `store_settings.data.themeWorkspaces[<themeId>]`, a SIBLING of the live
 * `themeSettings` row. That separation is the whole safety property: `saveTheme.ts` refuses to write the
 * live row while `themeStagedForPublish` is set, and this module only ever writes the workspace key — so a
 * seller merely trying a theme out can never repaint the store their customers see. Only `publishTheme()`
 * crosses the line, by design.
 *
 * Mirrors `saveTheme.ts` (debounce + max-wait + single-flight + offline backoff + tab-hide keepalive) minus
 * versioning: like the live theme row, a workspace is a single-writer last-write-wins document.
 *
 * READ PATH — deliberately synchronous. `syncThemeWorkspace()` runs after every load and must decide, with
 * no await, which settings to stage. So reads go through an in-memory {@link cache} that {@link
 * hydrateStagedTheme} fills in the background; a cache miss falls back to the theme registry's defaults
 * exactly as before, and the hydrated value is applied when it lands IF the seller has not edited since.
 */
import { getToken } from '../boot/session';
import { isReadOnlyView } from '../boot/view-handoff';
import { deriveApiBaseUrl } from '../boot/config';
import { builderApi } from '../lib/builderApi';
import { HttpError, isNetworkError } from '../lib/errors';
import type { ThemeSettings } from '@aquilixshop/ui-react';

export const WORKSPACE_DEBOUNCE_MS = 800;
export const WORKSPACE_MAX_WAIT_MS = 5000;
const BACKOFF_MIN_MS = 2000;
const BACKOFF_MAX_MS = 30_000;

/**
 * `themeId → staged settings`, or `null` for "the server confirmed this theme was never customized".
 * A MISSING key means "not fetched yet" — the three states are distinct and the read path depends on it
 * (absent → fall back to the registry AND hydrate; null → fall back to the registry, don't refetch).
 */
const cache = new Map<string, ThemeSettings | null>();

/** In-flight/queued hydrations, so N synchronous reads of the same theme fire ONE request. */
const hydrating = new Map<string, Promise<ThemeSettings | null>>();

// ── debounce-with-max-wait timer state (module-scoped singletons, mirror saveTheme.ts) ──
let debounceTimer: ReturnType<typeof setTimeout> | null = null;
let maxWaitTimer: ReturnType<typeof setTimeout> | null = null;
let backoffTimer: ReturnType<typeof setTimeout> | null = null;
let backoffMs = BACKOFF_MIN_MS;
let inFlight = false;
let pendingFollowUp = false;
/** The theme whose staged settings are unsaved; null = nothing pending. */
let pendingThemeId: string | null = null;

function clearDebounceTimers(): void {
  if (debounceTimer) {
    clearTimeout(debounceTimer);
    debounceTimer = null;
  }
  if (maxWaitTimer) {
    clearTimeout(maxWaitTimer);
    maxWaitTimer = null;
  }
}

function clearBackoff(): void {
  if (backoffTimer) {
    clearTimeout(backoffTimer);
    backoffTimer = null;
  }
}

function fire(): void {
  clearDebounceTimers();
  void flushThemeWorkspaceSave();
}

/**
 * The staged settings for a theme as currently known LOCALLY — synchronous by contract (see the module
 * doc). `null` means "nothing staged": either never customized, or not hydrated yet. Callers fall back to
 * the theme registry's own defaults in both cases.
 */
export function readStagedTheme(themeId: string): ThemeSettings | null {
  return cache.get(themeId) ?? null;
}

/** True once the server has answered for this theme — i.e. a registry fallback is now a real answer. */
export function isStagedThemeHydrated(themeId: string): boolean {
  return cache.has(themeId);
}

/**
 * Record an edit to a workspace's theme and schedule its debounced PUT. The cache is updated
 * SYNCHRONOUSLY so an immediate re-read (navigation within the workspace) never sees a stale value, even
 * if the network write is still pending or failing.
 */
export function stageThemeSettings(themeId: string, settings: ThemeSettings): void {
  cache.set(themeId, settings);
  pendingThemeId = themeId;
  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = setTimeout(fire, WORKSPACE_DEBOUNCE_MS);
  if (!maxWaitTimer) maxWaitTimer = setTimeout(fire, WORKSPACE_MAX_WAIT_MS);
}

/**
 * Fetch a workspace's staged settings once and cache the answer (including the "never customized" 404).
 * Concurrent callers share one request. Never throws — a failed hydration just leaves the cache empty, so
 * the caller keeps the registry defaults and a later entry retries.
 */
export async function hydrateStagedTheme(themeId: string): Promise<ThemeSettings | null> {
  const cached = cache.get(themeId);
  if (cached !== undefined) return cached;
  const existing = hydrating.get(themeId);
  if (existing) return existing;

  const p = (async (): Promise<ThemeSettings | null> => {
    try {
      const theme = await builderApi.getThemeWorkspace(themeId);
      cache.set(themeId, theme);
      return theme;
    } catch (e) {
      if (e instanceof HttpError && e.status === 404) {
        // CONTRACT, not an error: never customized. Adopt any pre-0034 localStorage staging exactly once
        // (the founder may have unpublished work there right now) and push it up, then forget the key.
        const migrated = adoptLegacyLocalStaging(themeId);
        cache.set(themeId, migrated);
        if (migrated) stageThemeSettings(themeId, migrated);
        return migrated;
      }
      return null; // transport/auth failure — leave the cache EMPTY so a later entry retries
    } finally {
      hydrating.delete(themeId);
    }
  })();
  hydrating.set(themeId, p);
  return p;
}

/**
 * Drop a CONSUMED workspace (publishTheme promoted it to the live row). Clears the cache synchronously and
 * best-effort deletes server-side — a failed delete only means the next "use this theme" resumes a stale
 * draft once more, never a failed publish.
 */
export function dropStagedTheme(themeId: string): void {
  cache.delete(themeId);
  if (pendingThemeId === themeId) {
    clearDebounceTimers();
    pendingThemeId = null;
  }
  void builderApi.deleteThemeWorkspace(themeId).catch(() => undefined);
}

/**
 * The core workspace save. Single-flight, last-write-wins. Resolves when this attempt settles; never
 * throws. A mid-flight edit is detected by a serialized marker so no edit is silently dropped.
 */
export async function flushThemeWorkspaceSave(): Promise<void> {
  clearDebounceTimers();
  if (isReadOnlyView()) return; // superadmin consultation: nothing may be written
  const themeId = pendingThemeId;
  if (!themeId) return; // nothing unsaved

  if (inFlight) {
    pendingFollowUp = true;
    return;
  }

  const theme = cache.get(themeId);
  if (!theme) {
    pendingThemeId = null;
    return;
  }
  inFlight = true;
  const marker = JSON.stringify(theme);

  try {
    await builderApi.saveThemeWorkspace(themeId, theme);
    backoffMs = BACKOFF_MIN_MS;
    clearBackoff();
    inFlight = false;
    const changed = JSON.stringify(cache.get(themeId)) !== marker;
    if (changed || pendingFollowUp) {
      pendingFollowUp = false;
      stageThemeSettings(themeId, cache.get(themeId) as ThemeSettings);
    } else if (pendingThemeId === themeId) {
      pendingThemeId = null;
    }
  } catch (e) {
    inFlight = false;
    // Keep `pendingThemeId` in every failure case: the staged value is only in memory, so dropping the
    // retry here is exactly the data loss this module exists to prevent.
    if (isNetworkError(e)) queueBackoffRetry();
  }
}

/** Exponential backoff retry while offline; the `online` trigger also resumes immediately. */
function queueBackoffRetry(): void {
  clearBackoff();
  backoffTimer = setTimeout(() => {
    backoffTimer = null;
    if (pendingThemeId) void flushThemeWorkspaceSave();
    backoffMs = Math.min(backoffMs * 2, BACKOFF_MAX_MS);
  }, backoffMs);
}

/**
 * Persist the staged workspace on a hard tab-close while a debounced edit is pending. `keepalive: true`
 * carries the Bearer header (unlike `sendBeacon`), so the PUT authenticates as the page goes away.
 * Returns true if a request was dispatched.
 *
 * Note the contrast with `keepaliveThemeFlush`, which deliberately REFUSES to fire while a theme is staged
 * for publish — that one targets the LIVE row. This one targets the workspace key, so firing is not just
 * safe but required: it is the tab-close path for the very edits that used to sit in localStorage.
 */
export function keepaliveThemeWorkspaceFlush(): boolean {
  clearDebounceTimers();
  if (isReadOnlyView()) return false; // superadmin consultation: nothing may be written
  const themeId = pendingThemeId;
  if (!themeId || inFlight) return false;
  const theme = cache.get(themeId);
  if (!theme) return false;

  const token = getToken();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  try {
    void fetch(
      `${deriveApiBaseUrl()}/api/store/settings/theme/workspaces/${encodeURIComponent(themeId)}`,
      {
        method: 'PUT',
        keepalive: true,
        credentials: 'include',
        headers,
        body: JSON.stringify(theme),
      },
    );
    pendingThemeId = null;
    return true;
  } catch {
    return false;
  }
}

/** Cancel any pending workspace save and forget every cached answer (store teardown / test reset). */
export function cancelThemeWorkspaceSave(): void {
  clearDebounceTimers();
  clearBackoff();
  hydrating.clear();
  cache.clear();
  pendingFollowUp = false;
  inFlight = false;
  pendingThemeId = null;
}

// ── force-flush trigger wiring (window events) — installed once by the shell ───

let triggersInstalled = false;

/**
 * Install the workspace force-flush window listeners. Idempotent (StrictMode double-mount → no-op).
 * Returns a teardown. Kept separate from `installThemeSaveTriggers` so the live-row engine and the
 * workspace engine stay decoupled — they have OPPOSITE tab-hide behaviour (see the note on
 * {@link keepaliveThemeWorkspaceFlush}) and must not be able to drift into each other.
 */
export function installThemeWorkspaceSaveTriggers(): () => void {
  if (triggersInstalled) return () => undefined;
  triggersInstalled = true;

  const onVisibility = (): void => {
    if (document.visibilityState === 'hidden') keepaliveThemeWorkspaceFlush();
  };
  const onPageHide = (): void => {
    keepaliveThemeWorkspaceFlush();
  };
  const onOnline = (): void => {
    clearBackoff();
    backoffMs = BACKOFF_MIN_MS;
    if (pendingThemeId) void flushThemeWorkspaceSave();
  };

  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('pagehide', onPageHide);
  window.addEventListener('online', onOnline);

  return () => {
    document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('pagehide', onPageHide);
    window.removeEventListener('online', onOnline);
    triggersInstalled = false;
  };
}

// ── one-time migration off localStorage ───────────────────────────────────────

/**
 * Pre-0034 staging key. Read ONCE per theme, on the server's "never customized" answer, then removed —
 * so unpublished work that exists on this machine today is adopted instead of silently dropped when the
 * source of truth moves server-side. Deliberately NOT a fallback: after this, localStorage is not read.
 */
const LEGACY_KEY_BASE = 'aqx-builder-staged-theme';

function adoptLegacyLocalStaging(themeId: string): ThemeSettings | null {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    // The legacy key was scoped by store id too; scan rather than rebuild it, so this works regardless of
    // whether the store id resolved yet on this boot.
    const suffix = `:${themeId}`;
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i);
      if (!key || !key.startsWith(LEGACY_KEY_BASE) || !key.endsWith(suffix)) continue;
      const raw = window.localStorage.getItem(key);
      window.localStorage.removeItem(key);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as ThemeSettings;
      return parsed && typeof parsed.presetId === 'string' ? parsed : null;
    }
    return null;
  } catch {
    return null;
  }
}
